/**
 * The board's toolbar (NexaCRM's arrangement: search, filter, sort, the
 * layout switch). Every control is the host's state -- usually the URL --
 * and every one narrows the SERVER's read; nothing here filters cards on the
 * client, so a column's count is always the count of what it shows.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import Segmented from '../../tide/Segmented';
import type { KanbanToolbarProps } from './types';

export const SEARCH_DEBOUNCE_MS = 250;

function SearchBox({ value, onChange, placeholder = 'Search', label = 'Search the board' }: NonNullable<KanbanToolbarProps['search']>) {
  const [draft, setDraft] = useState(value);
  const timer = useRef<number | null>(null);
  const id = useId();
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  const commit = (q: string, now = false) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (now) { onChange(q.trim()); return; }
    timer.current = window.setTimeout(() => { timer.current = null; onChange(q.trim()); }, SEARCH_DEBOUNCE_MS);
  };
  return (
    <div className="okb-search" role="search">
      <label htmlFor={id} className="okb-sr">{label}</label>
      <span className="okb-search__icon" aria-hidden="true" />
      <input id={id} type="search" value={draft} placeholder={placeholder} autoComplete="off"
        onChange={(e) => { setDraft(e.target.value); commit(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(draft, true); } }} />
      {draft && (
        <button type="button" className="okb-search__clear" onClick={() => { setDraft(''); commit('', true); }} aria-label="Clear the search">
          <span aria-hidden="true">&times;</span>
        </button>
      )}
    </div>
  );
}

export default function KanbanToolbar({ search, filters = [], sort, viewSwitch, summary, actions }: KanbanToolbarProps) {
  return (
    <div className="okb-toolbar">
      {search && <SearchBox {...search} />}
      {filters.map((f) => (
        <label key={f.key} className="okb-field">
          <span className="okb-field__label">{f.label}</span>
          <select value={f.value} onChange={(e) => f.onChange(e.target.value)}>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      ))}
      {sort && (
        <span className="okb-sort">
          <label className="okb-field">
            <span className="okb-field__label">Sort</span>
            <select value={sort.value} onChange={(e) => sort.onChange(e.target.value, sort.dir)}>
              {sort.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {sort.value && (
            <button type="button" className="okb-btn okb-btn--icon"
              aria-label={sort.dir === 'asc' ? 'Ascending: switch to descending' : 'Descending: switch to ascending'}
              onClick={() => sort.onChange(sort.value, sort.dir === 'asc' ? 'desc' : 'asc')}>
              <span aria-hidden="true">{sort.dir === 'asc' ? '↑' : '↓'}</span>
            </button>
          )}
        </span>
      )}
      {actions && <span className="okb-toolbar__actions">{actions}</span>}
      {summary && <span className="okb-toolbar__summary">{summary}</span>}
      {viewSwitch && <span className="okb-toolbar__switch">{viewSwitch}</span>}
    </div>
  );
}

/**
 * Kanban | Table. A view control, never a business act; the host keeps it in the URL (`layout=kanban`). Not "Board":
 * the workspaces already have a view called Board, and two controls of one name are one too many (2026-10-14).
 */
export function LayoutSwitch({ value, onChange, label = 'Layout' }: { value: 'kanban' | 'table'; onChange: (v: 'kanban' | 'table') => void; label?: string }) {
  return (
    <Segmented<'kanban' | 'table'>
      label={label}
      hideLabel
      className="okb-switch"
      value={value}
      onChange={onChange}
      options={[{ value: 'kanban', label: 'Kanban' }, { value: 'table', label: 'Table' }]}
    />
  );
}
